/**
 * @spell/network-protocol
 *
 * Every message that crosses the wire is declared here once and validated on
 * arrival by both sides. The server treats client input as hostile: nothing is
 * trusted until it parses, and even then the authoritative simulation
 * re-derives the outcome itself.
 */
import { z } from 'zod';
import { PRACTICE, PROJECTILE_SPEED_SCALE, PROTOCOL_VERSION } from '@spell/shared-types';
import { gesturePointSchema, spellDraftSchema, spellPublicSchema, spellSchema } from '@spell/spell-schema';

export { PROTOCOL_VERSION };

/* ------------------------------------------------------------------ */
/* Client -> Server                                                    */
/* ------------------------------------------------------------------ */

export const C2S = {
  PING: 'ping',
  IDENTIFY: 'identify',
  DEVICE_CHECK: 'deviceCheck',
  READY: 'ready',
  AIM: 'aim',
  MOVE: 'move',
  CAST: 'cast',
  CASTING: 'casting',
  SUBMIT_SPELL: 'submitSpell',
  REMATCH: 'rematch',
  RTC_SIGNAL: 'rtcSignal',
  PRACTICE_CONTROL: 'practiceControl',
} as const;

export const pingMsg = z.object({
  /** Client clock reading, in ms. */
  t: z.number().finite(),
  /**
   * The client's own most recent round-trip measurement. Only ever used to
   * widen the parry allowance, and clamped server-side, so inflating it buys
   * nothing beyond the published cap.
   */
  rtt: z.number().finite().nonnegative().max(5000).optional(),
});
export type PingMsg = z.infer<typeof pingMsg>;

export const identifyMsg = z.object({
  name: z.string().min(1).max(20),
  protocolVersion: z.number().int(),
  /** The parry spell the player built in the tutorial, restored from storage. */
  parrySpell: spellSchema.nullable(),
  /**
   * The attack spell the player authored in the lobby before entering.
   *
   * Both arrive from browser storage and are therefore untrusted: the server
   * re-owns and re-prices each of them from its effect list before it accepts
   * either into a book.
   */
  attackSpell: spellSchema.nullable(),
});
export type IdentifyMsg = z.infer<typeof identifyMsg>;

export const deviceCheckMsg = z.object({
  micGranted: z.boolean(),
  speechSupported: z.boolean(),
  /** True when the player is typing chants instead of speaking them. */
  usingFallbackInput: z.boolean(),
  gestureOk: z.boolean(),
  voiceLevel: z.number().min(0).max(1),
});
export type DeviceCheckMsg = z.infer<typeof deviceCheckMsg>;

export const aimMsg = z.object({
  yaw: z.number().finite(),
  pitch: z.number().finite(),
});
export type AimMsg = z.infer<typeof aimMsg>;

/**
 * Footwork: which way the duellist is holding, never where they think they are.
 *
 * A position on the wire would be a position the server had to either trust or
 * re-derive, and re-deriving it is the whole job anyway. So the client ships
 * intent in its own frame — `forward` is toward the opponent, `strafe` is to
 * its own right — and the room integrates it, clamps the speed and owns the
 * answer. A modified client can ask to walk in a direction; it cannot arrive
 * anywhere the box does not allow, and it cannot walk while casting.
 *
 * Sent on change and re-stated on a slow heartbeat, so a held key survives a
 * dropped packet without the client having to send every frame.
 */
export const moveMsg = z.object({
  forward: z.number().finite().min(-1).max(1),
  strafe: z.number().finite().min(-1).max(1),
});
export type MoveMsg = z.infer<typeof moveMsg>;

/**
 * A cast: everything the player did, and nothing about what they meant.
 *
 * There is no `spellId` here and no `intent`. Nothing selects a spell any more,
 * so the client genuinely does not know which one this was — it ships the raw
 * evidence and the server identifies it by scoring the whole book. That also
 * closes the old hole where the client nominated a spell and the server could
 * only re-grade the nomination.
 *
 * This is the only way to deal damage. There is no free attack to fall back on:
 * every shot costs a chant, a sigil and the exposure of performing both.
 */
export const castMsg = z.object({
  /** Whatever speech recognition heard, or what the player typed. */
  transcript: z.string().max(160),
  /** Raw pointer trail captured during the cast. */
  gesture: z.array(gesturePointSchema).max(2048),
  /**
   * Client-clock timestamp of the moment the microphone detected speech onset
   * (Web Audio RMS), *not* when recognition returned. Validated server-side.
   */
  speechStartAt: z.number().finite(),
  yaw: z.number().finite(),
  pitch: z.number().finite(),
});
export type CastMsg = z.infer<typeof castMsg>;

/**
 * Live telegraph while a chant is being spoken. Purely cosmetic — it makes a
 * long chant readable to the opponent as gathering runes, which is the
 * counterplay window a long chant is supposed to pay for.
 */
export const castingMsg = z.object({
  active: z.boolean(),
  progress: z.number().min(0).max(1),
  color: z.string().max(9),
});
export type CastingMsg = z.infer<typeof castingMsg>;

export const submitSpellMsg = spellDraftSchema.extend({
  slot: z.number().int().min(0).max(1),
});
export type SubmitSpellMsg = z.infer<typeof submitSpellMsg>;

export const rematchMsg = z.object({
  keepSpells: z.boolean(),
});
export type RematchMsg = z.infer<typeof rematchMsg>;

/**
 * Practice-room dials. Every field is optional so a single control can be
 * nudged without the client having to restate the rest, and every one of them
 * is clamped again server-side — the room, not the panel, owns these numbers.
 *
 * Ignored outside a practice room.
 */
export const practiceControlMsg = z.object({
  /** Whether the training dummy shoots at all. */
  dummyFire: z.boolean().optional(),
  intervalMs: z
    .number()
    .finite()
    .min(PRACTICE.FIRE_INTERVAL_MS.min)
    .max(PRACTICE.FIRE_INTERVAL_MS.max)
    .optional(),
  /** The room's projectile speed scale. Solo, so it may be changed live. */
  speedScale: z
    .number()
    .finite()
    .min(PROJECTILE_SPEED_SCALE.min)
    .max(PROJECTILE_SPEED_SCALE.max)
    .optional(),
  /** Refill health, mana and every cooldown on the spot. */
  restore: z.boolean().optional(),
});
export type PracticeControlMsg = z.infer<typeof practiceControlMsg>;

export const rtcSignalMsg = z.object({
  kind: z.enum(['offer', 'answer', 'candidate']),
  /** Opaque SDP / ICE payload, relayed verbatim to the other player. */
  payload: z.string().max(20_000),
});
export type RtcSignalMsg = z.infer<typeof rtcSignalMsg>;

/* ------------------------------------------------------------------ */
/* Server -> Client                                                    */
/* ------------------------------------------------------------------ */

export const S2C = {
  PONG: 'pong',
  WELCOME: 'welcome',
  SPELL_GRANTED: 'spellGranted',
  SPELL_BOOK: 'spellBook',
  CAST_RESULT: 'castResult',
  PARRY_RESULT: 'parryResult',
  IMPACT: 'impact',
  ROUND_RESULT: 'roundResult',
  MATCH_RESULT: 'matchResult',
  CRAFT_START: 'craftStart',
  OPPONENT_SPELL: 'opponentSpell',
  ANNOUNCE: 'announce',
  RTC_SIGNAL: 'rtcSignal',
  OPPONENT_CASTING: 'opponentCasting',
  ERROR: 'protocolError',
} as const;

export const pongMsg = z.object({
  t: z.number(),
  serverTime: z.number(),
});
export type PongMsg = z.infer<typeof pongMsg>;

export const welcomeMsg = z.object({
  sessionId: z.string(),
  roomCode: z.string(),
  isHost: z.boolean(),
  protocolVersion: z.number().int(),
});
export type WelcomeMsg = z.infer<typeof welcomeMsg>;

/**
 * The owner's complete spell book, sent whenever it changes.
 *
 * `spellGranted` announces a single new spell with its crafting notes; this
 * message is the authoritative list, so a client can never drift out of sync
 * with the server about which spells it owns — including the shared basic bolt,
 * which is never "crafted".
 */
export const spellBookMsg = z.object({
  spells: z.array(spellSchema),
});
export type SpellBookMsg = z.infer<typeof spellBookMsg>;

/** The full spell, delivered only to its owner. */
export const spellGrantedMsg = z.object({
  spell: spellSchema,
  slot: z.number().int(),
  notes: z.array(z.string()),
  interpreter: z.enum(['ai', 'local']),
});
export type SpellGrantedMsg = z.infer<typeof spellGrantedMsg>;

export const castResultMsg = z.object({
  /**
   * Which spell the server decided this was — an *answer*, not an echo of a
   * request. Empty when the evidence did not identify one, in which case
   * `reason` says whether it was unreadable or ambiguous.
   */
  spellId: z.string(),
  grade: z.enum(['Fail', 'Weak', 'Normal', 'Perfect']),
  gestureScore: z.number(),
  chantScore: z.number(),
  combined: z.number(),
  reason: z.string().nullable(),
  /** Server time the cast resolved, for client-side reconciliation. */
  at: z.number(),
});
export type CastResultMsg = z.infer<typeof castResultMsg>;

export const parryResultMsg = z.object({
  success: z.boolean(),
  grade: z.enum(['Fail', 'Weak', 'Normal', 'Perfect']),
  /** Signed timing error, ms. Negative = early. */
  timingError: z.number(),
  blockedDamage: z.number(),
  projectileId: z.string().nullable(),
  effects: z.array(z.string()),
  reason: z.string().nullable(),
  at: z.number(),
});
export type ParryResultMsg = z.infer<typeof parryResultMsg>;

export const impactMsg = z.object({
  targetId: z.string(),
  sourceId: z.string(),
  spellId: z.string(),
  amount: z.number(),
  kind: z.enum(['damage', 'heal', 'shield', 'reflect', 'absorb', 'dot']),
  x: z.number(),
  y: z.number(),
  z: z.number(),
  color: z.string(),
  lethal: z.boolean(),
});
export type ImpactMsg = z.infer<typeof impactMsg>;

export const roundResultMsg = z.object({
  round: z.number().int(),
  winnerId: z.string().nullable(),
  draw: z.boolean(),
  scores: z.record(z.string(), z.number()),
  nextPhase: z.string(),
});
export type RoundResultMsg = z.infer<typeof roundResultMsg>;

export const awardSchema = z.object({
  key: z.string(),
  title: z.string(),
  spellId: z.string(),
  chant: z.string(),
  ownerId: z.string(),
  value: z.number(),
  detail: z.string(),
});

export const matchResultMsg = z.object({
  winnerId: z.string().nullable(),
  scores: z.record(z.string(), z.number()),
  awards: z.array(awardSchema),
  spells: z.array(spellPublicSchema),
  /** Full spell documents, so each player can review what they built. */
  ownSpells: z.array(spellSchema),
  totalDamage: z.record(z.string(), z.number()),
  totalParries: z.record(z.string(), z.number()),
});
export type MatchResultMsg = z.infer<typeof matchResultMsg>;

export const craftStartMsg = z.object({
  slot: z.number().int(),
  endsAt: z.number(),
  round: z.number().int(),
});
export type CraftStartMsg = z.infer<typeof craftStartMsg>;

export const opponentSpellMsg = spellPublicSchema;
export type OpponentSpellMsg = z.infer<typeof opponentSpellMsg>;

/** Rune build-up telegraph while the opponent speaks a long chant. */
export const opponentCastingMsg = z.object({
  active: z.boolean(),
  /** 0..1 progress through the expected chant. */
  progress: z.number().min(0).max(1),
  runes: z.number().int().min(0).max(8),
  color: z.string(),
});
export type OpponentCastingMsg = z.infer<typeof opponentCastingMsg>;

export const announceMsg = z.object({
  kind: z.enum([
    'countdown', 'roundStart', 'roundEnd', 'craft', 'matchEnd',
    'opponentLeft', 'opponentReturned', 'aborted', 'waiting',
  ]),
  text: z.string(),
  seconds: z.number().optional(),
});
export type AnnounceMsg = z.infer<typeof announceMsg>;

export const protocolErrorMsg = z.object({
  message: z.string(),
  fatal: z.boolean(),
});
export type ProtocolErrorMsg = z.infer<typeof protocolErrorMsg>;

/* ------------------------------------------------------------------ */
/* Validation helper                                                   */
/* ------------------------------------------------------------------ */

/**
 * Parse an inbound payload, returning null instead of throwing. Rejected
 * messages are dropped: a malformed packet must never take a room down.
 */
export function safeDecode<T extends z.ZodTypeAny>(
  schema: T,
  payload: unknown,
): z.infer<T> | null {
  const result = schema.safeParse(payload);
  return result.success ? result.data : null;
}
